import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICategory, Icon } from '@bciti/types';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';
import { organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { State } from 'src/app/store';
import * as uuid from 'uuid';
import { BcitiColorSelectComponent } from '../color-select/bciti-color-select.component';
import { BcitiIconSelectComponent } from '../icon-select/bciti-icon-select.component';

export interface IFECategory extends ICategory {
    _id?: string;
    default: boolean;
}

export interface CategoryOutput {
    category: {
        name: {
            fr?: string;
            en?: string;
        };
        icon?: string;
        color?: string;
        _id?: string;
    };
    inputId: string;
}

export enum CategoryInputState {
    EDITING = 'EDITING',
    DELETING = 'DELETING',
    DISPLAYING = 'DISPLAYING',
}

export enum PropertyLabelInput {
    'CATEGORY' = 'CATEGORY',
    'STATUS' = 'STATUS',
    'AREA' = 'AREA',
}

@Component({
    selector: 'app-bciti-category-input',
    templateUrl: './bciti-category-input.component.html',
    styleUrls: ['./bciti-category-input.component.scss'],
})
export class BcitiCategoryInputComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    Icon = Icon;
    CategoryInputState = CategoryInputState;

    // state
    state: CategoryInputState = CategoryInputState.DISPLAYING;
    errors = false;

    loading = false;
    id: string;

    langs: string[];

    serviceToDeleteFirst: string[] = [];

    // replace category typage by AdminServiceCategory | AdminCollectCategory once both types are available
    @Input() category?: any;
    @Input() icons: string[];
    @Input() colors: string[];
    @Input() characterLimit: number;
    @Input() disabled = false;
    @Input() propertyLabelInput: PropertyLabelInput = PropertyLabelInput.CATEGORY;
    @Input() categoriesUsed: any;
    @Input() textInputHint: string = this.translate.instant('common.inputs.category-input.input-hint');
    @Input() canDelete: boolean = true;
    @Input() displayCloseButton: boolean = true;
    @Output() onSave = new EventEmitter<any>();
    @Output() onDelete = new EventEmitter<any>();
    @Output() cancelCreate = new EventEmitter();
    @Output() onFormOpen = new EventEmitter<string>();
    @Output() currentState = new EventEmitter<string>();

    @ViewChild(BcitiColorSelectComponent, { static: false })
    private colorSelector: BcitiColorSelectComponent;

    @ViewChild(BcitiIconSelectComponent, { static: false })
    private iconSelector: BcitiIconSelectComponent;

    categoryForm: any = this.fb.group(
        {
            name: this.fb.group({}),
        },
        {
            updateOn: 'blur',
        },
    );

    get categoryName() {
        return this.category?.name?.[this.translate.currentLang];
    }

    PropertyLabelInput = PropertyLabelInput;

    get propertyLabelInputTranslationsKey() {
        switch (this.propertyLabelInput) {
            case PropertyLabelInput.CATEGORY:
                return 'common.inputs.category-input.category.';
                break;
            case PropertyLabelInput.STATUS:
                return 'common.inputs.category-input.status.';
                break;
            case PropertyLabelInput.AREA:
                return 'common.inputs.category-input.area.';
                break;
            default:
                return 'common.inputs.category-input.category.';
        }
    }

    get currentLang(): string {
        return this.translate.currentLang;
    }

    hasError(lang: string) {
        return (
            (this.categoryForm.controls.name as FormGroup).controls[lang].invalid &&
            ((this.categoryForm.controls.name as FormGroup).controls[lang].touched || (this.categoryForm.controls.name as FormGroup).controls[lang].dirty)
        );
    }

    hasRequiredError(lang: string) {
        return (
            (this.categoryForm.controls.name as FormGroup).controls[lang].getError('required') &&
            ((this.categoryForm.controls.name as FormGroup).controls[lang].touched || (this.categoryForm.controls.name as FormGroup).controls[lang].dirty)
        );
    }

    constructor(
        private translate: TranslateService,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.id = uuid.v4();

        this.store
            .select(organisationConfigurationSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((config) => !!config),
                map((config) => {
                    this.langs = config?.lang?.available ?? [];
                    this.langs.forEach((lang) => {
                        (this.categoryForm.get('name') as FormGroup).addControl(lang, new FormControl('', [Validators.required]));
                        if (typeof this.characterLimit === 'number' && this.characterLimit > 0) {
                            (this.categoryForm.get('name') as FormGroup).controls[lang].setValidators([Validators.required, Validators.maxLength(this.characterLimit)]);
                            (this.categoryForm.get('name') as FormGroup).controls[lang].updateValueAndValidity();
                        }
                    });
                    // set this.langs array order to have currentLang as first index
                    this.langs = [this.currentLang, ...this.langs.filter((l) => this.currentLang !== l)];
                }),
            )
            .subscribe();

        if (!this.category) {
            this.state = CategoryInputState.EDITING;
            this.currentState.emit(this.state);
        }
        if (this.category?._id) {
            this.categoryForm.addControl('_id', new FormControl(this.category._id, []));
        }
        if (this.icons) {
            this.categoryForm.addControl('icon', new FormControl(this.category?.icon, [Validators.required]));
        }
        if (this.colors) {
            this.categoryForm.addControl('color', new FormControl(this.category?.color, [Validators.required]));
        }
    }

    edit() {
        this.onFormOpen.emit(this.id);
        this.categoryForm.reset();
        this.state = CategoryInputState.EDITING;
        this.currentState.emit(this.state);
        this.cdr.detectChanges();
        if (this.category.color) {
            this.categoryForm.patchValue({ color: this.category.color });
            this.colorSelector?.writeValue(this.category.color);
        }
        if (this.category.icon) {
            this.categoryForm.patchValue({ icon: this.category.icon });
            this.iconSelector?.writeValue(this.category.icon);
        }
        if (this.category.name) {
            this.categoryForm.patchValue({ name: this.category.name });
        }
        if (this.category._id) {
            this.categoryForm.patchValue({ _id: this.category._id });
        }
    }

    startDelete() {
        this.serviceToDeleteFirst = [];
        this.onFormOpen.emit(this.id);
        this.state = CategoryInputState.DELETING;
        this.currentState.emit(this.state);
    }

    cancelDelete() {
        this.state = CategoryInputState.DISPLAYING;
        this.serviceToDeleteFirst = [];
        this.currentState.emit(this.state);
    }

    confirmDelete() {
        this.onDelete.emit({
            category: this.category,
            inputId: this.id,
        });
    }

    save() {
        this.categoryForm.markAllAsTouched();
        this.categoryForm.updateValueAndValidity();
        if (this.categoryForm.valid) {
            this.onSave.emit({
                category: this.categoryForm.value,
                inputId: this.id,
            });
        }
    }

    cancelEdit() {
        if (!this.category) {
            // Emit event to parent to let them know we aren't creating anymore
            this.cancelCreate.emit(this.id);
        } else {
            this.state = CategoryInputState.DISPLAYING;
            this.currentState.emit(this.state);
            this.categoryForm.reset(this.category);
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
